/**
 * V1.1.1: Debug overlay for track transition metrics.
 * Shows fetch/decode/gap timing for each soundscape transition.
 * Only visible when ?debug=transitions is in URL.
 */
import { useState, useEffect, useRef } from 'react';
import soundscapeService from '../services/soundscapeService';

export default function TransitionDebug() {
  const [metrics, setMetrics] = useState([]);
  const [pos, setPos] = useState({ time: 0, dur: 0 });
  const rafRef = useRef(null);

  // Subscribe to new metrics
  useEffect(() => {
    setMetrics(soundscapeService.getTransitionMetrics());
    const unsub = soundscapeService.onTransitionMetric((entry) => {
      setMetrics(prev => [...prev.slice(-9), entry]);
    });
    return unsub;
  }, []);

  // Poll playback position for "time remaining" display
  useEffect(() => {
    const tick = () => {
      const dur = soundscapeService.getCurrentTrackDuration();
      const time = soundscapeService.getCurrentTrackPosition();
      setPos({ time, dur });
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  if (metrics.length === 0 && pos.dur === 0) return null;

  const remaining = Math.max(0, pos.dur - pos.time);
  const preloadReady = soundscapeService._preloadCache?.buffer ? true : false;

  return (
    <div
      data-testid="transition-debug-overlay"
      style={{
        position: 'fixed', bottom: 8, left: 8, zIndex: 99999,
        background: 'rgba(0,0,0,0.88)', color: '#0f0', fontFamily: 'monospace',
        fontSize: 11, padding: '6px 10px', borderRadius: 6,
        maxWidth: 420, maxHeight: 260, overflow: 'auto', lineHeight: 1.5,
        pointerEvents: 'none'
      }}
    >
      <div style={{ color: '#0ff', marginBottom: 2 }}>
        Track: {pos.dur > 0 ? `${pos.time.toFixed(1)}s / ${pos.dur.toFixed(1)}s  remaining=${remaining.toFixed(1)}s` : 'idle'}
        {' '}| Preload: <span style={{ color: preloadReady ? '#0f0' : '#f80' }}>{preloadReady ? 'READY' : soundscapeService._preloading ? 'LOADING...' : 'EMPTY'}</span>
      </div>
      <div style={{ borderTop: '1px solid #333', paddingTop: 2, marginTop: 2 }}>
        {metrics.length === 0 && <div style={{ color: '#888' }}>No transitions yet</div>}
        {metrics.map((m, i) => {
          const ok = m.startGapMs < 50;
          return (
            <div key={i} style={{ color: ok ? '#0f0' : '#f44', whiteSpace: 'nowrap' }}>
              {ok ? 'OK' : 'SLOW'} gap={m.startGapMs}ms fetch={m.fetchMs}ms dec={m.decodeMs}ms cache={m.fromCache ? 'Y' : 'N'} {m.trackName?.slice(0, 20)}
            </div>
          );
        })}
      </div>
    </div>
  );
}
